import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { createClientComponentClient } from '@/integrations/supabase/client'

const SNOOZE_KEY = 'sage_snooze_password_setup'
export function passwordSnoozed(): boolean {
  try { return sessionStorage.getItem(SNOOZE_KEY) === '1' } catch { return false }
}

export default function SetPasswordModal({ onDone }: { onDone?: () => void }) {
  const auth: any = useAuth()
  const user = auth?.user
  const supabase = useRef(createClientComponentClient()).current
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [ok, setOk] = useState(false)

  useEffect(() => { setErr(null) }, [pw, pw2])

  const snooze = () => {
    try { sessionStorage.setItem(SNOOZE_KEY, '1') } catch {}
    onDone?.()
  }

  const submit = async () => {
    if (pw.length < 8) { setErr('Password must be at least 8 characters.'); return }
    if (pw !== pw2)    { setErr('Passwords do not match.'); return }
    setBusy(true); setErr(null)
    try {
      const { error } = await supabase.auth.updateUser({ password: pw })
      if (error) throw error
      await (supabase as any).rpc('set_my_password_flag')
      auth?.refreshUserProfile?.().catch(() => {})
      setOk(true)
      setTimeout(snooze, 1800)
    } catch (e: any) {
      setErr(e?.message || 'Could not save. You can still sign in with your magic link.')
    } finally { setBusy(false) }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, color: '#fff',
    padding: '11px 12px', fontSize: 14, outline: 'none',
  }
  const labelStyle: React.CSSProperties = {
    display: 'block', color: 'rgba(255,255,255,0.55)', fontSize: 11,
    letterSpacing: 1, marginBottom: 4, textTransform: 'uppercase',
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
      <div style={{ width: '100%', maxWidth: 420, background: '#0f172a', borderRadius: 16,
                    border: '1px solid rgba(255,255,255,0.10)', padding: 'clamp(20px, 5vw, 28px)',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
        {ok ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{ fontSize: 40 }}>🔐</div>
            <h3 style={{ color: '#fff', margin: '10px 0 6px', fontSize: 18 }}>Password saved</h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: 0 }}>
              Next time, sign in with your email and password — no email link needed.
            </p>
          </div>
        ) : (
          <>
            <h3 style={{ color: '#fff', margin: '0 0 6px', fontSize: 18 }}>Set your password</h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, lineHeight: 1.5, margin: '0 0 16px' }}>
              You signed in with a magic link. Add a password so you can log in directly
              {user?.email ? <> as <b style={{ color: 'rgba(255,255,255,0.9)' }}>{user.email}</b></> : null}.
            </p>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Password</label>
              <input type={show ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)}
                autoComplete="new-password" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Confirm password</label>
              <input type={show ? 'text' : 'password'} value={pw2} onChange={(e) => setPw2(e.target.value)}
                autoComplete="new-password" style={inputStyle} />
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'rgba(255,255,255,0.5)',
                            fontSize: 12, marginBottom: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
              Show passwords
            </label>
            {err && <div style={{ color: '#fca5a5', fontSize: 12, marginBottom: 12 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={snooze} disabled={busy}
                style={{ flex: '1 1 auto', minHeight: 40, borderRadius: 10, cursor: 'pointer',
                         border: '1px solid rgba(255,255,255,0.15)', background: 'transparent',
                         color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>Maybe later</button>
              <button onClick={submit} disabled={busy}
                style={{ flex: '2 1 auto', minHeight: 40, borderRadius: 10, cursor: 'pointer',
                         border: 'none', background: 'var(--org-accent, #3b82f6)', color: '#fff',
                         fontWeight: 700, fontSize: 13 }}>
                {busy ? 'Saving…' : 'Save password'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}